import { useLayoutEffect, useMemo, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';

import { DebugClearStorageButton } from "@/components/shared/DebugClearStorageButton";
import type { GameInstructionsScreenProps } from '@micro-screens/src/types/game-instructions-screen';
import type {
  InstructionIconKey,
  InstructionSlide,
  StageInstructionContent,
} from '@micro-screens/src/types/instruction-content';
import type { StageId } from '@micro-screens/src/types/stage-theme';
import { STAGE_THEME_COLORS } from '@micro-screens/theme/colors';
import { getCloudFrontAssetUrl } from '@utils/asset-utils';

import { INSTRUCTION_ICONS } from './instruction-icons';
import { INSTRUCTION_SCREEN_CONTENT_MAP } from './instruction-screen-content-map';
import './instructions-screen.css';

const STAGE_OVERLAYS: Record<StageId, string> = {
  1: getCloudFrontAssetUrl('stage-1/Stage_1.png'),
  2: getCloudFrontAssetUrl('stage-2/Stage_2.png'),
  3: getCloudFrontAssetUrl('stage-3/Stage_3.png'),
  4: getCloudFrontAssetUrl('stage-4/Stage_4.png'),
  5: getCloudFrontAssetUrl('stage-5/Stage_5.png'),
  6: getCloudFrontAssetUrl('stage-6/Stage_6.png'),
  7: getCloudFrontAssetUrl('stage-7/Stage_7.png'),
};

const DEFAULT_SCREEN_HEADING = 'How to Play?';

/**
 * Per-move presentation defaults, applied by position.
 *
 * Slides usually arrive from `game-metadata.json`, which carries copy only — no
 * icon, accent, or tag. These fill that gap so every game gets the styled moves
 * without touching its metadata; any slide may still override each field.
 */
const MOVE_PRESETS: ReadonlyArray<{
  accent: string;
  icon: InstructionIconKey;
  tagLabel: string;
}> = [
  { accent: '#18B16C', icon: 'tickCircle', tagLabel: 'BASICS' },
  { accent: '#F8C39B', icon: 'winner', tagLabel: 'SCORING' },
  { accent: '#CCE4FF', icon: 'danger', tagLabel: 'ANTI CHEAT' },
];

const FALLBACK_PRESET = {
  accent: 'rgba(255, 255, 255, 0.45)',
  icon: 'closeCircle' as InstructionIconKey,
  tagLabel: '',
};

const getMovePreset = (index: number) => MOVE_PRESETS[index] ?? FALLBACK_PRESET;

const resolveGameLabel = (labelTemplate: string, stage: StageId): string =>
  labelTemplate.includes('{stage}')
    ? labelTemplate.replace('{stage}', String(stage))
    : labelTemplate;

/** `MOVE 01`, `MOVE 02`, … — used when a slide declares no `moveLabel`. */
const defaultMoveLabel = (index: number): string =>
  `MOVE ${String(index + 1).padStart(2, '0')}`;

// ─── InstructionMove ────────────────────────────────────────────────────────

interface InstructionMoveProps {
  readonly slide: InstructionSlide;
  readonly index: number;
}

function InstructionMove({ slide, index }: Readonly<InstructionMoveProps>) {
  const preset = getMovePreset(index);
  const accent = slide.accent ?? preset.accent;
  const tagLabel = slide.tagLabel ?? preset.tagLabel;
  const iconSrc = INSTRUCTION_ICONS[slide.icon ?? preset.icon];

  return (
    <li className="move" style={{ '--move-accent': accent } as React.CSSProperties}>
      <div className="moveHeader">
        <span className="moveMarker" aria-hidden />
        <span className="moveLabel">{slide.moveLabel ?? defaultMoveLabel(index)}</span>
        {tagLabel ? <span className="moveTag">{tagLabel}</span> : null}
      </div>

      <div className="moveCard">
        <div className="moveCardHead">
          {iconSrc ? (
            <span className="moveIcon">
              <img src={iconSrc} alt="" aria-hidden />
            </span>
          ) : null}
          <div className="moveCardHeadText">
            <h3 className="moveCardTitle">{slide.title}</h3>
            {slide.description ? <p className="moveCardDescription">{slide.description}</p> : null}
          </div>
        </div>

        {slide.items.length > 0 ? (
          <ul className="movePoints">
            {slide.items.map((item) => (
              <li className="movePoint" key={item.id}>
                <span className="movePointBullet" aria-hidden />
                {/* Points render description-only; a point with no description
                    (e.g. fair-play rules) falls back to its title as the text. */}
                <p className="movePointText">{item.description || item.title}</p>
              </li>
            ))}
          </ul>
        ) : null}
      </div>
    </li>
  );
}

// ─── GameInstructionsScreen ─────────────────────────────────────────────────

export function GameInstructionsScreen({
  stage,
  contentByStage,
  onPlayNow,
  onLearnHowToPlay,
  hideLearnHowToPlay = false,
  className,
}: Readonly<GameInstructionsScreenProps>) {
  const { i18n, t } = useTranslation();
  const theme = STAGE_THEME_COLORS[stage];
  const overlay = STAGE_OVERLAYS[stage];

  const content = useMemo(() => {
    const i18nRaw = i18n.t('instructions.stageInstructions', { returnObjects: true });
    const i18nContent =
      typeof i18nRaw !== 'string' ? (i18nRaw as Partial<StageInstructionContent>) : undefined;
    const base = {
      ...INSTRUCTION_SCREEN_CONTENT_MAP[stage],
      ...(i18nContent ?? {}),
    };
    const overrideContent = contentByStage?.[stage];
    if (!overrideContent) return base;
    return {
      ...base,
      ...overrideContent,
      slides: overrideContent.slides,
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [contentByStage, stage, i18n.language]);

  // The CTAs float over the scroll area rather than sitting in flow, so the
  // scroll area's bottom padding is measured live from their real height —
  // it has to track hideLearnHowToPlay (one button vs. two) automatically.
  const ctaRef = useRef<HTMLDivElement | null>(null);
  const [ctaHeight, setCtaHeight] = useState(0);

  useLayoutEffect(() => {
    const el = ctaRef.current;
    if (!el) return;

    const measure = () => { setCtaHeight(el.offsetHeight); };
    measure();

    const resizeObserver = new ResizeObserver(measure);
    resizeObserver.observe(el);
    return () => { resizeObserver.disconnect(); };
  }, []);

  return (
    <div className="instructionViewport">
      <section
        className={`${className ?? ''} instructionRoot`.trim()}
        style={{ backgroundColor: theme.background }}
        aria-label="Game instructions screen"
      >
        <img src={overlay} alt="" aria-hidden className="overlay" />
        <div
          className="eclipse"
          style={{ '--eclipse-color': theme.eclipse } as React.CSSProperties}
        />

        <div
          className="content"
          style={{ '--cta-height': `${String(ctaHeight)}px` } as React.CSSProperties}
        >
          <div className="game-header relative">
            <div className="metaRow">
              <span className="metaText">{resolveGameLabel(content.gameLabel, stage)}</span>
              <span className="statusPill">{content.statusLabel}</span>
            </div>
            <h1 className="gameTitle">{content.gameTitle}</h1>
            {/* TODO(temp): debug-only reset button — visible on every screen */}
            <DebugClearStorageButton />
          </div>

          <h2 className="screenHeading">{content.screenHeading ?? DEFAULT_SCREEN_HEADING}</h2>

          <div className="movesScroll">
            <ol className="moveList">
              {content.slides.map((slide, index) => (
                <InstructionMove key={slide.id} slide={slide} index={index} />
              ))}
            </ol>
          </div>

          <div className="ctaStack" ref={ctaRef}>
            <button
              className="button buttonPrimary"
              type="button"
              onClick={() => onPlayNow?.(stage)}
            >
              {content.playNowButton ?? t('instructions.labels.playNow')}
            </button>
            {!hideLearnHowToPlay && (
              <button
                className="button buttonSecondary"
                type="button"
                onClick={() => onLearnHowToPlay?.(stage)}
              >
                {content.learnHowToPlay ?? t('instructions.labels.learnHowToPlay')}
              </button>
            )}
          </div>
        </div>
      </section>
    </div>
  );
}
